import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { ApiClient } from '@center-technologies/shared-api-clients';
import type { Context } from 'types/context';
import {
  listContexts,
  getContext,
  createContext as createContextApi,
  updateContext as updateContextApi,
  deleteContext as deleteContextApi,
  cloneContext as cloneContextApi,
  type CreateContextPayload,
  type UpdateContextPayload,
  type CloneContextPayload,
} from 'design-system/my-lib';

interface ContextsState {
  list: Context[];
  current: Context | null;
  loading: boolean;
  error: string | null;
}

const initialState: ContextsState = {
  list: [],
  current: null,
  loading: false,
  error: null,
};

// Org scope is derived from the token server-side — the thunk takes no organizationId.
export const fetchContexts = createAsyncThunk<
  Context[],
  { apiClient: ApiClient },
  { rejectValue: string }
>(
  'contexts/fetchContexts',
  async ({ apiClient }, thunkAPI) => {
    try {
      return await listContexts(apiClient);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to fetch contexts';
      return thunkAPI.rejectWithValue(msg);
    }
  }
);

export const fetchContext = createAsyncThunk<
  Context,
  { apiClient: ApiClient; systemName: string },
  { rejectValue: string }
>(
  'contexts/fetchContext',
  async ({ apiClient, systemName }, thunkAPI) => {
    try {
      return await getContext(apiClient, systemName);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to fetch context';
      return thunkAPI.rejectWithValue(msg);
    }
  }
);

export const createContextThunk = createAsyncThunk<
  Context,
  { apiClient: ApiClient; payload: CreateContextPayload },
  { rejectValue: string }
>(
  'contexts/createContext',
  async ({ apiClient, payload }, thunkAPI) => {
    try {
      const result = await createContextApi(apiClient, payload);
      return result.context;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to create context';
      return thunkAPI.rejectWithValue(msg);
    }
  }
);

export const updateContextThunk = createAsyncThunk<
  Context,
  { apiClient: ApiClient; systemName: string; payload: UpdateContextPayload },
  { rejectValue: string }
>(
  'contexts/updateContext',
  async ({ apiClient, systemName, payload }, thunkAPI) => {
    try {
      const result = await updateContextApi(apiClient, systemName, payload);
      return result.context;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to update context';
      return thunkAPI.rejectWithValue(msg);
    }
  }
);

export const deleteContextThunk = createAsyncThunk<
  string,
  { apiClient: ApiClient; systemName: string },
  { rejectValue: string }
>(
  'contexts/deleteContext',
  async ({ apiClient, systemName }, thunkAPI) => {
    try {
      await deleteContextApi(apiClient, systemName);
      return systemName;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to delete context';
      return thunkAPI.rejectWithValue(msg);
    }
  }
);

export const cloneContextThunk = createAsyncThunk<
  Context,
  { apiClient: ApiClient; systemName: string; payload: CloneContextPayload },
  { rejectValue: string }
>(
  'contexts/cloneContext',
  async ({ apiClient, systemName, payload }, thunkAPI) => {
    try {
      const result = await cloneContextApi(apiClient, systemName, payload);
      return result.context;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to clone context';
      return thunkAPI.rejectWithValue(msg);
    }
  }
);

const contextsSlice = createSlice({
  name: 'contexts',
  initialState,
  reducers: {
    clearCurrentContext: (state) => {
      state.current = null;
    },
    clearContextsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchContexts
      .addCase(fetchContexts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchContexts.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload;
      })
      .addCase(fetchContexts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Failed to fetch contexts';
      })
      // fetchContext
      .addCase(fetchContext.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchContext.fulfilled, (state, action) => {
        state.loading = false;
        state.current = action.payload;
      })
      .addCase(fetchContext.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Failed to fetch context';
      })
      // createContext
      .addCase(createContextThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createContextThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.list.push(action.payload);
        state.current = action.payload;
      })
      .addCase(createContextThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Failed to create context';
      })
      // updateContext
      .addCase(updateContextThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateContextThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.current = action.payload;
        const idx = state.list.findIndex(
          (c) => c.system_name === action.payload.system_name
        );
        if (idx !== -1) {
          state.list[idx] = action.payload;
        }
      })
      .addCase(updateContextThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Failed to update context';
      })
      // deleteContext
      .addCase(deleteContextThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteContextThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.list = state.list.filter(
          (c) => c.system_name !== action.payload
        );
        if (state.current?.system_name === action.payload) {
          state.current = null;
        }
      })
      .addCase(deleteContextThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Failed to delete context';
      })
      // cloneContext
      .addCase(cloneContextThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cloneContextThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.list.push(action.payload);
      })
      .addCase(cloneContextThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Failed to clone context';
      });
  },
});

export const { clearCurrentContext, clearContextsError } = contextsSlice.actions;

// Selectors
export const selectContextsList = (state: { contexts: ContextsState }) =>
  state.contexts.list;
export const selectCurrentContext = (state: { contexts: ContextsState }) =>
  state.contexts.current;
export const selectContextsLoading = (state: { contexts: ContextsState }) =>
  state.contexts.loading;
export const selectContextsError = (state: { contexts: ContextsState }) =>
  state.contexts.error;

export default contextsSlice.reducer;
